import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, MessageCircle } from 'lucide-react';
import {
  PRODUCT_OPTIONS,
  ARTWORK_OPTIONS,
  URGENCY_OPTIONS,
  buildQuoteMessage,
  isQuoteComplete,
} from '@/data/quote';
import { whatsappLink } from '@/data/site';

const QuoteContext = createContext({ openQuote: () => {} });
export const useQuote = () => useContext(QuoteContext);

const EMPTY = { product: '', details: '', quantity: '', size: '', artwork: '', urgency: '', name: '', email: '' };

const FIELD =
  'mt-2 w-full border border-ink/25 bg-white px-4 py-3 text-base text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

function Chips({ name, legend, options, value, onChange, required }) {
  return (
    <fieldset>
      <legend className="eyebrow text-ink/70">
        {legend}
        {required && <span className="text-brand"> *</span>}
      </legend>
      <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label={legend}>
        {options.map((o) => {
          const active = value === o.id;
          return (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={active}
              data-testid={`quote-${name}-${o.id}`}
              onClick={() => onChange(active && !required ? '' : o.id)}
              className={`border px-4 py-2.5 text-sm font-medium transition-colors ${
                active
                  ? 'border-brand bg-brand text-white'
                  : 'border-ink/25 bg-white text-ink hover:border-ink'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export function QuoteProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const openQuote = useCallback((product = '') => {
    setForm((f) => ({ ...f, product: product || f.product }));
    setOpen(true);
  }, []);
  const value = useMemo(() => ({ openQuote }), [openQuote]);

  const set = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));
  const onInput = (key) => (e) => set(key)(e.target.value);
  const complete = isQuoteComplete(form);

  const submit = (e) => {
    e.preventDefault();
    if (!complete) return;
    window.open(whatsappLink(buildQuoteMessage(form)), '_blank', 'noopener,noreferrer');
    setOpen(false);
    setForm(EMPTY);
  };

  return (
    <QuoteContext.Provider value={value}>
      {children}
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/70" />
          <Dialog.Content
            data-testid="quote-dialog"
            className="fixed inset-0 z-[61] overflow-y-auto bg-paper text-ink sm:inset-auto sm:left-1/2 sm:top-1/2 sm:max-h-[92vh] sm:w-[calc(100%-3rem)] sm:max-w-2xl sm:-translate-x-1/2 sm:-translate-y-1/2"
          >
            <form onSubmit={submit} className="p-6 sm:p-10">
              <Dialog.Close
                aria-label="Zatvori"
                className="absolute right-4 top-4 p-2 text-ink/70 hover:text-ink"
              >
                <X size={24} />
              </Dialog.Close>
              <span className="eyebrow flex items-center gap-3 text-ink/60">
                <span className="h-px w-8 bg-brand" aria-hidden="true" />
                Zahtjev za ponudu
              </span>
              <Dialog.Title className="mt-3 text-3xl leading-[1.05] sm:text-4xl">
                Recite nam šta vam treba.
              </Dialog.Title>
              <Dialog.Description className="mt-3 text-ink/75 leading-relaxed">
                Nekoliko kratkih pitanja, a mi vam odgovaramo brže i s tačnom ponudom. Poruka se otvara
                u WhatsAppu, gdje možete dodati fotografije ili fajlove.
              </Dialog.Description>

              <div className="mt-8 space-y-6">
                <Chips
                  name="product"
                  legend="Šta trebate?"
                  required
                  options={PRODUCT_OPTIONS}
                  value={form.product}
                  onChange={set('product')}
                />

                <label className="block">
                  <span className="eyebrow text-ink/70">
                    Kratak opis <span className="text-brand">*</span>
                  </span>
                  <textarea
                    data-testid="quote-details"
                    rows={3}
                    value={form.details}
                    onChange={onInput('details')}
                    placeholder="npr. baner za fasadu, majice s logom za 20 radnika…"
                    className={FIELD}
                  />
                </label>

                <div className="grid gap-6 sm:grid-cols-2">
                  <label className="block">
                    <span className="eyebrow text-ink/70">Broj komada</span>
                    <input
                      data-testid="quote-quantity"
                      type="text"
                      inputMode="numeric"
                      value={form.quantity}
                      onChange={onInput('quantity')}
                      placeholder="npr. 50"
                      className={FIELD}
                    />
                  </label>
                  <label className="block">
                    <span className="eyebrow text-ink/70">Dimenzije (ako znate)</span>
                    <input
                      data-testid="quote-size"
                      type="text"
                      value={form.size}
                      onChange={onInput('size')}
                      placeholder="npr. 3 × 1 m"
                      className={FIELD}
                    />
                  </label>
                </div>

                <Chips
                  name="artwork"
                  legend="Imate li pripremu?"
                  options={ARTWORK_OPTIONS}
                  value={form.artwork}
                  onChange={set('artwork')}
                />
                <Chips
                  name="urgency"
                  legend="Koliko je hitno?"
                  options={URGENCY_OPTIONS}
                  value={form.urgency}
                  onChange={set('urgency')}
                />

                <div className="grid gap-6 sm:grid-cols-2">
                  <label className="block">
                    <span className="eyebrow text-ink/70">Vaše ime ili firma</span>
                    <input
                      data-testid="quote-name"
                      type="text"
                      autoComplete="organization"
                      value={form.name}
                      onChange={onInput('name')}
                      className={FIELD}
                    />
                  </label>
                  <label className="block">
                    <span className="eyebrow text-ink/70">Email</span>
                    <input
                      data-testid="quote-email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={onInput('email')}
                      placeholder="ime@firma.ba"
                      className={FIELD}
                    />
                  </label>
                </div>
              </div>

              <button
                type="submit"
                data-testid="quote-submit"
                disabled={!complete}
                className="mt-10 inline-flex w-full items-center justify-center gap-2 bg-brand px-7 py-4 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
              >
                <MessageCircle size={18} aria-hidden="true" />
                Pošalji upit na WhatsApp
              </button>
              {!complete && (
                <p className="mt-3 text-sm text-ink/60">Odaberite vrstu proizvoda i unesite kratak opis.</p>
              )}
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </QuoteContext.Provider>
  );
}
